import { createContext, FC, useCallback, useContext, useEffect, useState } from "react";
import getConfig from 'next/config';

const { publicRuntimeConfig: { genericSubDomain, baseProtocol, baseDomain, basePort, enableEncryptedPacks } } = getConfig();

const XdStorageContext = createContext(null);

export const XdStorageProvider: FC = ({ children }) => {
  const [ storage, setStorage ] = useState({});
  const [ isLoaded, setIsLoaded ] = useState<boolean>(false)

  const getItem = useCallback(async (name: string) => {
    if (!enableEncryptedPacks) {
      return;
    }
     
    return window.localStorage.getItem(name);
  }, []);

  const getKeys = useCallback(async () => {
    if (!enableEncryptedPacks) {
      return;
    }

    const keys: string[] = [];

    for (let x = 0; x < window.localStorage.length; x++) {
      keys.push(window.localStorage.key(x));
    }

    return keys;
  }, []);

  const load = useCallback(async () => {
    if (!enableEncryptedPacks) {
      setIsLoaded(true);
      return;
    }

    const keys = await getKeys() as string[];
    const storage = {};

    for (const key of keys) {
      storage[key] = await getItem(key);
    }

    setStorage(storage);
    setIsLoaded(true);
  }, [ getKeys, setIsLoaded, setStorage, getItem ]);

  const setItem = useCallback(async (name: string, value: any) => {
    if (!enableEncryptedPacks) {
      return;
    }

    window.localStorage.setItem(name, value);
    await load();
  }, [ load ]);

  const removeItem = useCallback(async (name: string) => {
    if (!enableEncryptedPacks) {
      return;
    }

    window.localStorage.removeItem(name);
    await load();
  }, [ load ]);

  useEffect(() => {
    load();
  }, [ load, setIsLoaded ]);

  return (
    <XdStorageContext.Provider value={{ isLoaded, setItem, getItem, removeItem, storage }}>
      {children}
    </XdStorageContext.Provider>
  )
};

export const useXdStorageContext = () => useContext(XdStorageContext);
